// @flow

import * as React from 'react';
import BoardCoverEdit from 'app/common/react/components/BoardForm/BoardCoverEdit';
import BoardFormList from 'app/common/react/components/BoardForm/BoardFormList';
import BoardFormRow from 'app/common/react/components/BoardForm/BoardFormRow';
import CONST from 'app/common/lib/Const';
import ga from 'app/common/lib/ga';
import i18n from 'app/common/lib/i18n';
import Modal from 'app/common/react/ui/Modal';
import type { BoardPrivacy } from 'app/packages/pinterest-flow-types/board';
import type { ImagePositionType } from 'app/common/react/components/profile/ProfileBoardCard/commonTypes';
import { Experiment } from 'app/common/react/components/experiments';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Link,
  SelectList,
  Switch,
  Text,
  TextArea,
  TextField,
} from 'gestalt';
import { Required, Length, getErrors, type Validator } from 'app/common/lib/react/validator';

export type FieldValues = {|
  name: string,
  description: string,
  category: ?string,
  collab_board_email: boolean,
  collaborator_invites_enabled: boolean,
  privacy: BoardPrivacy,
  allow_homefeed_recommendations: boolean,
|};

type Props = {|
  board: {
    allow_homefeed_recommendations: boolean,
    archived_by_me_at: string,
    category: ?string,
    collab_board_email: boolean,
    collaborator_invites_enabled: boolean,
    description: string,
    name: string,
    id: string,
    is_collaborative: boolean,
    owner: {
      full_name: string,
      id: string,
      image_key: string,
      image_medium_url: string,
      username: string,
    },
    pin_count: number,
    privacy: BoardPrivacy,
  },
  boardCollaboratorsSection: ?React.Element<any>,
  categories: Array<{
    key: string,
    name: string,
  }>,
  coverImageHeight?: number,
  coverImageWidth?: number,
  currentUserIsOwner: boolean,
  gaCategory: string,
  isAdAccountOwner?: boolean,
  isArchivable?: boolean,
  isDeletable: boolean,
  isEditable: boolean,
  // isNameAndSecretView is used to toggle a simplified view of the board create form with only those 2 fields
  isNameAndSecretView?: boolean,
  noHeader?: boolean,
  onClose: () => void,
  onCropChange?: (imageUrl: string, imagePosition: ImagePositionType) => void,
  onArchiveButtonClick?: () => void,
  onDeleteButtonClick?: () => void,
  onMergeButtonClick?: () => void,
  onSubmit: (values: FieldValues) => Promise<any>,
  onUnarchiveButtonClick?: () => void,
  submitText: string,
  title: React.Node,
|};

type State = {|
  category: ?string,
  collab_board_email: boolean,
  collaboratorInvitesEnabled: boolean,
  description: string,
  descriptionValidatorErrors: Array<string>,
  name: string,
  nameValidatorErrors: Array<string>,
  pin_count: number,
  privacy: BoardPrivacy,
  allowHomefeedRecommendations: boolean,
  showBoardCoverEdit: boolean,
  showDeleteConfirmation: boolean,
  submitError?: string,
  submitting: boolean,
|};

export default class BoardForm extends React.Component<Props, State> {
  static defaultProps = {
    isNameAndSecretView: false,
  };

  constructor(props: Props, context: *) {
    super(props, context);
    const {
      board: {
        category,
        collab_board_email,
        collaborator_invites_enabled,
        description,
        name,
        pin_count,
        privacy,
        allow_homefeed_recommendations,
      },
    } = props;

    this.state = {
      category,
      collab_board_email,
      description,
      name,
      pin_count,
      privacy,
      allowHomefeedRecommendations: allow_homefeed_recommendations,
      collaboratorInvitesEnabled: collaborator_invites_enabled,
      nameValidatorErrors: [],
      descriptionValidatorErrors: [],
      showBoardCoverEdit: false,
      showDeleteConfirmation: false,
      submitting: false,
    };

    // =================================================================================================
    // Staging translations: Replacing validators with the versions in app/packages/validators/validator
    // These validators must be passed error messages explicitly
    // See CUI-3594
    // Length({ max: CONST.MAX_CHARS_FOR_BOARD_TITLE, message }) -> Update usages for correct maxLength
    interpolateNamedTemplate(
      i18n._(
        'Please enter no more than {{ maxLength }} characters.',
        'Validation error when a string is too long',
      ),
      { maxLength: CONST.MAX_CHARS_FOR_BOARD_TITLE },
    );
    // =================================================================================================

    this.descriptionValidators = [Length({ max: CONST.MAX_CHARS_FOR_BOARD_PIN_DESCRIPTION })];

    this.nameValidators = [
      Required({ message: i18n._("Don't forget to name your board!") }),
      Length({ max: CONST.MAX_CHARS_FOR_BOARD_TITLE }),
    ];
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.board.name !== this.props.board.name) {
      const nameValidatorErrors = getErrors(this.nameValidators, nextProps.board.name);
      this.setState({ name: nextProps.board.name, nameValidatorErrors });
    }
    if (nextProps.board.description !== this.props.board.description) {
      const descriptionValidatorErrors = getErrors(
        this.descriptionValidators,
        nextProps.board.description,
      );
      this.setState({ description: nextProps.board.description, descriptionValidatorErrors });
    }
    if (nextProps.board.category !== this.props.board.category) {
      this.setState({ category: nextProps.board.category });
    }
    if (nextProps.board.privacy !== this.props.board.privacy) {
      this.setState({ privacy: nextProps.board.privacy });
    }
  }

  getBoardTitleInput() {
    const { name, nameValidatorErrors } = this.state;

    return (
      <Box role="listitem">
        <BoardFormRow label={i18n._('Name')} labelId="boardEditName">
          <TextField
            errorMessage={nameValidatorErrors.join(', ')}
            id="boardEditName"
            name="boardName"
            onChange={this.handleChangeName}
            placeholder={i18n._('Like "Places to Go" or "Recipes to Make"')}
            value={name}
          />
        </BoardFormRow>
      </Box>
    );
  }

  getCategoriesDropdown() {
    const categories = this.props.categories.map((category) => {
      const label = category.name;
      const value = category.key;
      return { label, value };
    });

    const placeholder = i18n._('What kind of board is it?');

    const category = this.state.category || null;

    return (
      <Box role="listitem">
        <BoardFormRow label={i18n._('Category')} labelId="boardEditCategory">
          <SelectList
            id="boardEditCategory"
            name="category"
            onChange={this.handleChangeCategory}
            options={categories}
            placeholder={placeholder}
            value={category}
          />
        </BoardFormRow>
      </Box>
    );
  }

  getCollaboratorsEmailCheckbox() {
    const { board } = this.props;
    const { collab_board_email } = this.state;

    return (
      board.is_collaborative && (
        <Box role="listitem">
          <BoardFormRow label={i18n._('Email notifications')} labelId="emailNotificationsCheckbox">
            <Box alignItems="center" deprecatedPadding={{ y: 2 }} display="flex">
              <Box deprecatedMargin={{ right: 1 }}>
                <Switch
                  switched={collab_board_email}
                  id="emailNotificationsCheckbox"
                  name="email"
                  onChange={this.handleChangeCollabBoardEmail}
                />
              </Box>
              <Text size="xs" smSize="xs" mdSize="xs" lgSize="xs">
                {i18n._('Get an email when someone saves Pins to this board')}
              </Text>
            </Box>
          </BoardFormRow>
        </Box>
      )
    );
  }

  getCollaboratorsInviteList() {
    const { boardCollaboratorsSection } = this.props;

    const label = (
      <Box>
        {i18n._('Collaborators')}
        <Box>
          <Link
            target="blank"
            href={CONST.GEN.templateConst.settings.HELP_BOARDS_INVITE_URL_SRC_CREATE}
          >
            <Text size="sm" smSize="xs" mdSize="xs" lgSize="sm">
              {i18n._('Learn more')}
            </Text>
          </Link>
        </Box>
      </Box>
    );

    return !!boardCollaboratorsSection ? (
      <Box role="listitem">
        <BoardFormRow alignTop label={label} labelId="collaborators">
          <Box paddingY={3}>
            <div className="boardCollaboratorsWrapper">{boardCollaboratorsSection}</div>
          </Box>
        </BoardFormRow>
      </Box>
    ) : null;
  }

  getCollaboratorsInviteToggle() {
    const { board } = this.props;
    const { collaboratorInvitesEnabled } = this.state;

    return (
      board.is_collaborative && (
        <Box role="listitem">
          <BoardFormRow
            label={i18n._('Let collaborators add people')}
            labelId="collaboratorInvitesEnabledCheckbox"
          >
            <Box deprecatedPadding={{ y: 2 }}>
              <Switch
                switched={collaboratorInvitesEnabled}
                id="collaboratorInvitesEnabledCheckbox"
                name="collaboratorInvitesEnabled"
                onChange={this.handleChangeCollaboratorInvitesEnabled}
              />
            </Box>
          </BoardFormRow>
        </Box>
      )
    );
  }

  getCoverEdit() {
    const { board, coverImageHeight, coverImageWidth, isEditable } = this.props;
    const { name, pin_count: pinCount } = this.state;
    const showCoverEdit = isEditable && pinCount > 0;

    return (
      showCoverEdit && (
        <Box role="listitem">
          <BoardFormRow label={i18n._('Cover')}>
            <Button
              inline
              onClick={this.handleShowBoardCoverEditModal}
              text={i18n._('Change')}
              size="md"
            />
            {this.state.showBoardCoverEdit && (
              <Modal
                accessibilityCloseLabel={i18n._(
                  'close',
                  'accessible label for button to close modal',
                )}
                accessibilityModalLabel={i18n._(
                  'Change Board Cover',
                  'accessible label for modal to change board cover image',
                )}
                size="lg"
                heading={`${i18n._('Change Board Cover')}/ ${board.name}`}
                onDismiss={this.handleHideBoardCoverEditModal}
              >
                <BoardCoverEdit
                  board={board}
                  boardName={name}
                  frameHeight={coverImageHeight}
                  frameWidth={coverImageWidth}
                  onCancel={this.handleHideBoardCoverEditModal}
                  onSave={this.handleSaveBoardCoverEditModal}
                />
              </Modal>
            )}
          </BoardFormRow>
        </Box>
      )
    );
  }

  getDescriptionInput() {
    const { description, descriptionValidatorErrors } = this.state;
    const id = 'boardEditDescription';

    return (
      <Box role="listitem">
        <BoardFormRow alignTop label={i18n._('Description')} labelId={id}>
          <TextArea
            errorMessage={descriptionValidatorErrors.join(', ')}
            id={id}
            name="description"
            onChange={this.handleChangeDescription}
            placeholder={i18n._("What's your board about?")}
            value={description}
          />
        </BoardFormRow>
      </Box>
    );
  }

  getFormFooter() {
    const {
      board: { archived_by_me_at },
      isArchivable,
      isDeletable,
      onArchiveButtonClick,
      onDeleteButtonClick,
      onMergeButtonClick,
      onUnarchiveButtonClick,
      submitText,
    } = this.props;
    const { submitError, submitting } = this.state;
    return (
      <Box paddingX={4} paddingY={2}>
        <Box alignItems="center" justifyContent="between" wrap display="flex">
          <Box display="flex" alignItems="center" paddingY={2} marginLeft={-2} marginRight={-2}>
            {isDeletable && (
              <Box paddingX={2} data-test-id="deleteBoardButton">
                <Button
                  disabled={submitting}
                  inline
                  onClick={onDeleteButtonClick}
                  size="md"
                  text={i18n._('Delete', 'button label during board edit')}
                />
              </Box>
            )}
            {isArchivable &&
              !archived_by_me_at && (
                <Box paddingX={2}>
                  <Button
                    disabled={submitting}
                    inline
                    onClick={onArchiveButtonClick}
                    size="md"
                    text={i18n._('Archive', 'button label during board edit')}
                  />
                </Box>
              )}
            {archived_by_me_at && (
              <Box paddingX={2}>
                <Button
                  disabled={submitting}
                  inline
                  onClick={onUnarchiveButtonClick}
                  size="md"
                  text={i18n._('Unarchive', 'button label during board edit')}
                />
              </Box>
            )}
            {onMergeButtonClick && (
              <Box paddingX={2}>
                <Button
                  disabled={submitting}
                  inline
                  onClick={onMergeButtonClick}
                  size="md"
                  text={i18n._('Merge', 'button label during board edit')}
                />
              </Box>
            )}
          </Box>
          <Box>
            <Box display="flex" paddingY={2} marginLeft={-2} marginRight={-2}>
              <Box paddingX={2}>
                <Button inline onClick={this.handleCancel} text={i18n._('Cancel')} size="md" />
              </Box>
              <Box paddingX={2}>
                <Button
                  color="red"
                  disabled={this.submitDisabled()}
                  inline
                  text={submitText}
                  type="submit"
                  size="md"
                />
              </Box>
            </Box>
          </Box>
        </Box>
        {submitError && (
          <Box paddingX={1} paddingY={2}>
            <Text color="red" smSize="xs" mdSize="sm" lgSize="md">
              {submitError}
            </Text>
          </Box>
        )}
      </Box>
    );
  }

  getSecretToggle() {
    const { isAdAccountOwner } = this.props;
    const { privacy } = this.state;

    // NOTE: we only want to show this protected board permanent toggle if isAdAccountOwner
    // is explicitly "false". All other cases can fallback to normal logic below
    if (isAdAccountOwner === false) {
      return (
        <Box role="listitem">
          <BoardFormRow
            label={i18n._("Protected board (not listed on user's profile)", 'protected board')}
            labelId="protected"
          >
            <Box deprecatedPadding={{ y: 2 }}>
              <Switch onChange={() => undefined} id="protected" switched disabled />
            </Box>
          </BoardFormRow>
        </Box>
      );
    }

    // If a board is protected, do not allow them to toggle the secret state
    // this can cause a state where a board cannot being edited
    if (privacy !== CONST.BoardPrivacy.PROTECTED) {
      const label = <Box>{i18n._('Visibility')}</Box>;

      return (
        <Box role="listitem">
          <BoardFormRow label={label} labelId="secret">
            <Box display="flex" paddingY={2} alignItems="center">
              <Box marginRight={3}>
                <Checkbox
                  checked={privacy === 'secret'}
                  id="secret"
                  name="secret"
                  onChange={this.handleChangePrivateToggle}
                />
              </Box>
              <Box>
                <Text size="sm" smSize="xs" mdSize="xs" lgSize="sm">
                  {i18n._(
                    'Keep this board secret.',
                    'Caption for board settings to set visibility',
                  )}
                </Text>
                <Link
                  target="blank"
                  href={CONST.GEN.templateConst.settings.HELP_BOARDS_SECRET_URL_SRC_CREATE}
                >
                  <Text size="sm" smSize="xs" mdSize="xs" lgSize="sm" bold>
                    {i18n._('Learn more')}
                  </Text>
                </Link>
              </Box>
            </Box>
          </BoardFormRow>
        </Box>
      );
    }

    return null;
  }

  getRecommendationToggle() {
    const { allowHomefeedRecommendations } = this.state;

    const label = <Box>{i18n._('Personalization')}</Box>;

    return (
      <Box role="listitem">
        <BoardFormRow label={label} labelId="recommendation">
          <Box display="flex" paddingY={2} alignItems="center">
            <Box marginRight={3}>
              <Checkbox
                checked={allowHomefeedRecommendations}
                id="recommendation"
                name="recommendation"
                onChange={this.handleChangeRecommendationToggle}
              />
            </Box>
            <Box>
              <Text size="sm" smSize="xs" mdSize="xs" lgSize="sm">
                {i18n._(
                  'Show Pins inspired by this board in your home feed.',
                  'Caption for board settings to set visibility',
                )}
              </Text>
            </Box>
          </Box>
        </BoardFormRow>
      </Box>
    );
  }

  getBoardFormFields() {
    const {
      board: { archived_by_me_at },
      currentUserIsOwner,
      isNameAndSecretView,
    } = this.props;

    if (!currentUserIsOwner) {
      return (
        <BoardFormList>
          {this.getCollaboratorsInviteList()}
          {this.getCollaboratorsEmailCheckbox()}
        </BoardFormList>
      );
    } else if (isNameAndSecretView) {
      return (
        <BoardFormList>
          {this.getBoardTitleInput()}
          {this.getSecretToggle()}
        </BoardFormList>
      );
    } else {
      return (
        <BoardFormList>
          {this.getBoardTitleInput()}
          {this.getDescriptionInput()}
          {this.getCategoriesDropdown()}
          {this.getCoverEdit()}
          {this.getSecretToggle()}
          {!archived_by_me_at && (
            <Experiment name="web_homefeed_control_board_recommendation_toggle">
              {({ anyEnabled }) => anyEnabled && this.getRecommendationToggle()}
            </Experiment>
          )}
          {this.getCollaboratorsInviteList()}
          {this.getCollaboratorsEmailCheckbox()}
          {this.getCollaboratorsInviteToggle()}
        </BoardFormList>
      );
    }
  }

  _nameField: TextField;
  descriptionValidators: Array<Validator>;
  nameValidators: Array<Validator>;

  handleChangeName = ({ value: name }: { value: string }) => {
    const nameValidatorErrors = getErrors(this.nameValidators, name);

    this.setState({ name, nameValidatorErrors });
  };

  handleChangeDescription = ({ value: description }: { value: string }) => {
    const descriptionValidatorErrors = getErrors(this.descriptionValidators, description);

    this.setState({ description, descriptionValidatorErrors });
  };

  handleChangeCategory = ({ value }: { value: string }) => {
    // checking the placeholder wasn't "selected"
    if (value !== 'true') {
      this.setState({ category: value });
    }
  };

  handleChangePrivateToggle = ({ checked: isPrivate }: { checked: boolean }) => {
    this.setState({ privacy: isPrivate ? 'secret' : 'public' });
  };

  handleChangeRecommendationToggle = ({ checked: isEnabled }: { checked: boolean }) => {
    this.setState({ allowHomefeedRecommendations: isEnabled });
  };

  handleChangeCollabBoardEmail = ({ value: isEnabled }: { value: boolean }) => {
    this.setState({ collab_board_email: isEnabled });
  };

  handleChangeCollaboratorInvitesEnabled = ({ value: isEnabled }: { value: boolean }) => {
    this.setState({ collaboratorInvitesEnabled: isEnabled });
  };

  handleCancel = () => {
    ga.trackEvent(this.props.gaCategory, PCONST.GA_ACTION_CLICK_CANCEL);
    this.props.onClose();
  };

  handleSubmit = (event: SyntheticEvent<>) => {
    const { onSubmit } = this.props;
    const {
      category,
      collab_board_email,
      collaboratorInvitesEnabled,
      description: rawDescription,
      name: rawName,
      privacy,
      allowHomefeedRecommendations,
    } = this.state;

    if (event) {
      // If event exists, it's an event from the form submit dispatched by a ReactDOMComponent.
      // We don't want to redirect the webpage through an enter event through the form.
      event.preventDefault();
    }
    if (this.submitDisabled()) {
      return;
    }

    const name = rawName.trim();
    const description = rawDescription.trim();

    this.setState({ submitting: true, submitError: '' });

    // TODO(bca): Make sure data for this board_id is consistent everywhere
    ga.trackEvent(this.props.gaCategory, PCONST.GA_ACTION_CLICK_SUBMIT);
    onSubmit({
      name,
      category,
      collab_board_email,
      collaborator_invites_enabled: collaboratorInvitesEnabled,
      description,
      privacy,
      allow_homefeed_recommendations: allowHomefeedRecommendations,
    })
      .then(() => {
        this.setState({ submitting: false });
      })
      .catch((error) => {
        let submitError = i18n._('Uh oh! Something went wrong.');
        if (error.message) {
          const { message: summary, message_detail: detail } = error;
          submitError = detail ? `${summary} ${detail}` : summary;
        } else if (typeof error === 'string') {
          submitError = error;
        }
        this.setState({ submitting: false, submitError });
      });
  };

  handleShowBoardCoverEditModal = () => {
    this.setState({ showBoardCoverEdit: true });
  };

  handleHideBoardCoverEditModal = () => {
    this.setState({ showBoardCoverEdit: false });
  };

  handleSaveBoardCoverEditModal = (imageUrl: string, imagePosition: ImagePositionType) => {
    this.handleHideBoardCoverEditModal();
    const { onCropChange } = this.props;
    if (onCropChange) {
      onCropChange(imageUrl, imagePosition);
    }
  };

  submitDisabled() {
    const { descriptionValidatorErrors, name, nameValidatorErrors, submitting } = this.state;
    return (
      !name || nameValidatorErrors.length > 0 || descriptionValidatorErrors.length > 0 || submitting
    );
  }

  render() {
    const { noHeader } = this.props;
    return (
      <Box
        color="white"
        deprecatedMargin={{ left: 'auto', right: 'auto' }}
        maxWidth={600}
        shape="rounded"
      >
        <form method="post" onSubmit={this.handleSubmit}>
          {!noHeader && (
            <React.Fragment>
              <Box deprecatedMargin={{ right: 5 }} deprecatedPadding={{ x: 2, y: 3 }}>
                {this.props.title}
              </Box>
              <Divider />
            </React.Fragment>
          )}
          {this.getBoardFormFields()}
          {this.getFormFooter()}
        </form>
      </Box>
    );
  }
}
